import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  useSyncExternalStore,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { cx } from "./basics";

export interface MenuItem {
  label: ReactNode;
  onSelect: () => void;
  danger?: boolean;
  /** Only in the menu on phones: on wider screens the page shows its own button for it. */
  mobileOnly?: boolean;
  /** Shown but not choosable (greyed, skipped by the arrow keys). */
  disabled?: boolean;
  /** The option that is the case now: marked for assistive tech and set in bold. */
  current?: boolean;
}

/** Phone widths, where the menu opens as a bottom sheet instead of a dropdown. */
export const MENU_SHEET_QUERY = "(max-width: 767.98px)";
/** Space kept between the dropdown and the viewport's edges. */
export const MENU_MARGIN = 8;
/** Space between the trigger and the dropdown. */
const GAP = 4;

export interface MenuPlacement {
  top: number;
  left: number;
  /** Set when the panel is taller than the room on either side: it scrolls instead. */
  maxHeight?: number;
  /** Set when the panel is wider than the viewport allows. */
  maxWidth?: number;
  /** Which side of the trigger the panel opened on. */
  side: "below" | "above";
}

type Rect = Pick<DOMRect, "top" | "bottom" | "left" | "right">;

/**
 * Where to put a dropdown of `panel` size for a trigger at `trigger` (viewport coordinates),
 * keeping it fully inside the viewport with `margin` to spare. It lines up with the trigger's
 * right edge, opens rightwards from the trigger's left edge when that would run off the left
 * (UX-153: a "⋯" near the left edge pushed the panel off screen), and is clamped either way.
 * It opens below, flips above when there's no room below, and scrolls when neither side fits.
 */
export function placeMenu(
  trigger: Rect,
  panel: { width: number; height: number },
  viewport: { width: number; height: number },
  margin = MENU_MARGIN,
): MenuPlacement {
  const room = viewport.width - 2 * margin;
  const width = Math.min(panel.width, room);
  let left = trigger.right - width;
  if (left < margin) left = trigger.left;
  left = Math.max(margin, Math.min(left, viewport.width - margin - width));

  const below = viewport.height - margin - (trigger.bottom + GAP);
  const above = trigger.top - GAP - margin;
  let side: MenuPlacement["side"] = "below";
  let maxHeight: number | undefined;
  if (panel.height > below) {
    if (panel.height <= above) side = "above";
    else {
      side = above > below ? "above" : "below";
      maxHeight = Math.max(0, side === "above" ? above : below);
    }
  }
  const height = maxHeight ?? panel.height;
  const top = side === "below" ? trigger.bottom + GAP : trigger.top - GAP - height;
  return {
    top: Math.max(margin, top),
    left,
    side,
    ...(maxHeight !== undefined && { maxHeight }),
    ...(width < panel.width && { maxWidth: room }),
  };
}

function subscribeSheet(onChange: () => void) {
  const q = window.matchMedia?.(MENU_SHEET_QUERY);
  q?.addEventListener?.("change", onChange);
  return () => q?.removeEventListener?.("change", onChange);
}

/** True at phone widths, where the menu is a bottom sheet; follows the window as it's resized. */
function useSheet(): boolean {
  return useSyncExternalStore(
    subscribeSheet,
    () => window.matchMedia?.(MENU_SHEET_QUERY).matches ?? false,
    () => false,
  );
}

const viewportSize = () => ({
  width: document.documentElement.clientWidth || window.innerWidth,
  height: window.innerHeight,
});

/** A "⋯" overflow menu: a button that opens a short list of actions. Closes on a choice,
 * Escape or a click outside. The list is portalled to <body> so no clipping ancestor can hide
 * it: a dropdown placed by `placeMenu` on wider screens, a bottom sheet over a backdrop on
 * phones. Arrow keys, Home and End move between the items. */
export function Menu({
  label = "More actions",
  trigger = "⋯",
  items,
  className,
  triggerClassName = "btn menu-t",
  triggerLabel,
  disabled,
  triggerCurrent,
}: {
  /** Names the list; also the trigger unless `triggerLabel` says otherwise. */
  label?: string;
  trigger?: ReactNode;
  items: (MenuItem | false | null | undefined)[];
  className?: string;
  /** The trigger's classes. The default is the "⋯" button; a page can style its own. */
  triggerClassName?: string;
  /** The trigger's accessible name when it should say more than the list's. */
  triggerLabel?: string;
  /** The trigger can't be pressed. */
  disabled?: boolean;
  /** The trigger stands for the current step of a path (aria-current="step"). */
  triggerCurrent?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [place, setPlace] = useState<MenuPlacement | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const btn = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const id = useId();
  const sheet = useSheet();
  const shown = items.filter((i): i is MenuItem => !!i);

  const close = useCallback((refocus: boolean) => {
    setOpen(false);
    setPlace(null);
    if (refocus) btn.current?.focus();
  }, []);

  const position = useCallback(() => {
    const t = btn.current;
    const p = panel.current;
    if (!t || !p) return;
    setPlace(
      placeMenu(
        t.getBoundingClientRect(),
        { width: p.offsetWidth, height: p.scrollHeight || p.offsetHeight },
        viewportSize(),
      ),
    );
  }, []);

  // Place the dropdown before it paints, and keep it with its trigger on scroll and resize.
  useLayoutEffect(() => {
    if (!open || sheet) return;
    position();
    window.addEventListener("resize", position);
    window.addEventListener("scroll", position, true);
    return () => {
      window.removeEventListener("resize", position);
      window.removeEventListener("scroll", position, true);
    };
  }, [open, sheet, position]);

  // Focus the first item so the keyboard can carry on from the trigger, once the list is
  // visible (a hidden element can't take focus, and the dropdown is hidden until placed).
  const ready = open && (sheet || place !== null);
  useEffect(() => {
    if (!ready) return;
    const first = Array.from(
      panel.current?.querySelectorAll<HTMLElement>("[role=menuitem]:not([disabled])") ?? [],
    ).find((el) => getComputedStyle(el).display !== "none");
    first?.focus({ preventScroll: true });
  }, [ready]);

  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => {
      const target = e.target as Node;
      if (!ref.current?.contains(target) && !panel.current?.contains(target)) close(false);
    };
    const key = (e: KeyboardEvent) => {
      if (e.key === "Escape") close(true);
    };
    // On phones the backdrop takes the outside tap (on click, so the tap can't land on the page).
    if (!sheet) document.addEventListener("mousedown", away);
    document.addEventListener("keydown", key);
    return () => {
      document.removeEventListener("mousedown", away);
      document.removeEventListener("keydown", key);
    };
  }, [open, sheet, close]);

  if (!shown.length) return null;
  const desktopEmpty = shown.every((i) => i.mobileOnly);

  const onKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    // Phone-only items are display:none on wider screens: skip them.
    const all = Array.from(
      panel.current?.querySelectorAll<HTMLElement>("[role=menuitem]:not([disabled])") ?? [],
    ).filter((el) => getComputedStyle(el).display !== "none");
    if (!all.length) return;
    const at = all.indexOf(document.activeElement as HTMLElement);
    const go = (i: number) => {
      e.preventDefault();
      all[(i + all.length) % all.length]?.focus();
    };
    if (e.key === "ArrowDown") go(at + 1);
    else if (e.key === "ArrowUp") go(at < 0 ? -1 : at - 1);
    else if (e.key === "Home") go(0);
    else if (e.key === "End") go(-1);
    else if (e.key === "Tab") close(false);
  };

  const style: CSSProperties | undefined = sheet
    ? undefined
    : place
      ? {
          top: place.top,
          left: place.left,
          maxHeight: place.maxHeight,
          maxWidth: place.maxWidth,
        }
      : { top: 0, left: 0, visibility: "hidden" };

  const list = (
    <div
      ref={panel}
      id={id}
      role="menu"
      aria-label={label}
      className={cx("menu-l", sheet ? "menu-sheet" : "menu-float", place?.side === "above" && "up")}
      style={style}
      onKeyDown={onKeyDown}
    >
      {shown.map((item, i) => (
        <button
          key={i}
          type="button"
          role="menuitem"
          className={cx(
            "menu-i",
            item.danger && "danger",
            item.mobileOnly && "mob-only",
            item.current && "current",
          )}
          disabled={item.disabled}
          aria-current={item.current ? "true" : undefined}
          onClick={() => {
            // Focus goes back to the trigger first, so a dialog the item opens returns it there.
            close(true);
            item.onSelect();
          }}
        >
          {item.label}
        </button>
      ))}
    </div>
  );

  return (
    <div ref={ref} className={cx("menu", desktopEmpty && "mob-only", className)}>
      <button
        ref={btn}
        type="button"
        className={triggerClassName}
        aria-label={triggerLabel ?? label}
        disabled={disabled}
        aria-current={triggerCurrent ? "step" : undefined}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        onClick={() => (open ? close(false) : setOpen(true))}
      >
        {trigger}
      </button>
      {open &&
        createPortal(
          sheet ? (
            <div
              className="menu-back"
              data-testid="menu-backdrop"
              onClick={(e) => {
                if (e.target === e.currentTarget) close(true);
              }}
            >
              {list}
            </div>
          ) : (
            list
          ),
          document.body,
        )}
    </div>
  );
}
